import { inject } from '@angular/core';
import { type WritableDraft } from 'immer';
import { map, type Observable, of } from 'rxjs';

import { type CanvasElementFrom, type PartialPropsOf } from '@blaze-runtime/engine-domain';
import { applyPropsPatch } from '@blaze-runtime/engine-domain';

import { type EngineState } from '../../../+state/engine';
import {
  AbstractAiElementHandler,
  addSourceWorkflow,
  applyFieldSize,
  createElement,
  getElement,
  patchFieldLabel,
  toOptionPickerDefaultValue,
} from '../../../ai';
import { ComponentSourceWorkflowService } from '../../../ai/workflow';

import { type DropdownContext, type DropdownResolvedContext } from './dropdown.definition';

export class DropdownElementHandler extends AbstractAiElementHandler<'dropdown'> {
  private readonly componentSourceWorkflowService = inject(ComponentSourceWorkflowService);

  readonly type = 'dropdown';

  prepare(context: DropdownContext): Observable<DropdownResolvedContext> {
    this.resolveElementIcon(context.element);

    const workflowEntry = this.resolveWorkflowContext(context);

    if (!workflowEntry) {
      return of(context);
    }

    return this.componentSourceWorkflowService
      .ensureCreated(workflowEntry, context.projectId, context.pageId)
      .pipe(map((workflowContext) => ({ ...context, workflowContext })));
  }

  process(context: DropdownResolvedContext, draft: WritableDraft<EngineState>): void {
    let props: PartialPropsOf<'dropdown'> = {};
    props = patchFieldLabel<'dropdown'>(props, context.element);

    if (context.element.placeholder !== undefined) {
      props = applyPropsPatch<'dropdown'>(props, {
        content: { menu: { placeholder: context.element.placeholder } },
      });
    }

    if (context.element.defaultValue !== undefined) {
      props = applyPropsPatch<'dropdown'>(props, {
        content: {
          data: { defaultValue: toOptionPickerDefaultValue(context.element.defaultValue) },
        },
      });
    }

    if (context.element.isReadOnly !== undefined) {
      props = applyPropsPatch<'dropdown'>(props, {
        content: { data: { readonly: context.element.isReadOnly } },
      });
    }

    if (context.element.displayProperties?.sortBy !== undefined) {
      props = applyPropsPatch<'dropdown'>(props, {
        content: { sort: { field: context.element.displayProperties.sortBy, direction: 'asc' } },
      });
    }

    const { elementId } = createElement(draft, context, props);
    applyFieldSize<'dropdown'>(getElement(draft, elementId, 'dropdown'), context.element);

    if (context.workflowContext) {
      addSourceWorkflow(draft, context.workflowContext.workflow);
    }
  }

  finalize(
    element: CanvasElementFrom<'dropdown'>,
    context: DropdownResolvedContext,
  ): Observable<void> {
    if (!context.workflowContext) {
      return of(undefined);
    }

    return this.finalizeSourceWorkflow(element, context.workflowContext, 'optionsList');
  }
}
